import {
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core'
import { Close, ContactSupport } from '@material-ui/icons'
import { Alert } from '@material-ui/lab'
import Fuse from 'fuse.js'
import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

import Page from '@aletheia/common/components/Page'

import Markdown from '../../components/Markdown'
import {
  IFaq,
  fetchSeries,
  isFaq,
  isEntry,
  fetchTags,
  ITag,
} from '../../contentful'

const useStyles = makeStyles((theme) => ({
  filterGrid: {
    [theme.breakpoints.down('sm')]: {
      order: -1,
    },
  },
  filters: {
    [theme.breakpoints.up('md')]: {
      position: 'sticky',
      alignSelf: 'flex-start',
      height: 'auto',
      top: 100,
    },
  },
  searchBox: {
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  tagWrap: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0, 0.5, 1, 0),
    },
  },
  faq: {
    marginBottom: theme.spacing(4),
  },
  faqTitle: {
    color: theme.palette.primary.dark,
  },
  faqLink: {
    cursor: 'pointer',
    textDecoration: 'none',
    color: theme.palette.primary.main,
    '&:visited': {
      color: theme.palette.primary.main,
    },
  },
  faqBody: {
    marginTop: theme.spacing(1),
    '& h4, & h3, & h2': {
      fontSize: '1.25rem',
      marginBottom: theme.spacing(0.5),
    },
  },
}))

type FAQPageProps = {
  FAQs: IFaq[]
  Tags: ITag[]
}

const fuseOptions = {
  keys: ['fields.title', 'fields.body'],
  threshold: 0.5,
}

const FAQPage: React.FC<FAQPageProps> = ({ FAQs, Tags }) => {
  const classes = useStyles()

  const [search, setSearch] = useState('')
  const [tags, setTags] = useState<Record<string, boolean>>({})

  const { asPath } = useRouter()
  const [expanded, setExpanded] = useState(
    asPath.includes('#') ? asPath.split('#')[1] : '',
  )

  const [fuse, setFuse] = useState(new Fuse<IFaq>(FAQs, fuseOptions))

  const filteredAndSorted = filterAndSort(FAQs, fuse, search, tags)

  // update Fuse, only triggered in development
  useEffect(() => {
    setFuse(new Fuse<IFaq>(FAQs, fuseOptions))
  }, [FAQs])

  return (
    <Page title="Frequently Asked Questions" variant="wide">

      <Grid spacing={4} container>
        <Grid item xs={12} md={8}>
          {filteredAndSorted.length === 0 && (
            <>
              <p>Your filters do not match any of our FAQs.</p>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearch('')
                  setTags({})
                }}
              >
                Clear Filters
              </Button>
            </>
          )}

          {filteredAndSorted.map((Faq) => {
            return (
              <div className={classes.faq} key={Faq.sys.id}>
                <Typography
                  variant="h4"
                  id={Faq.sys.id}
                  className={classes.faqTitle}
                >
                  {expanded !== Faq.sys.id ? (
                    <a
                      href={`#${Faq.sys.id}`}
                      className={classes.faqLink}
                      onClick={() => {
                        setExpanded(Faq.sys.id)
                      }}
                    >
                      {Faq.fields.title}
                    </a>
                  ) : (
                    Faq.fields.title
                  )}
                </Typography>
                {expanded === Faq.sys.id && (
                  <Typography variant="body1" className={classes.faqBody}>
                    <Markdown simpleHeadings>{Faq.fields.body}</Markdown>
                  </Typography>
                )}
              </div>
            )
          })}
        </Grid>
        <Grid item xs={12} md={4} className={classes.filterGrid}>
          <div className={classes.filters}>
            <TextField
              className={classes.searchBox}
              label="Search"
              variant="outlined"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              InputProps={{
                endAdornment: search.length > 0 && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="erase search"
                      onClick={() => {
                        setSearch('')
                      }}
                      edge="end"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <div className={classes.tagWrap}>
              {Tags.sort((a, b) => {
                if (!a || !b) return 0
                return a.fields.title > b.fields.title ? 1 : -1
              }).map((Tag) => {
                if (!Tag) return null
                return (
                  <Chip
                    key={Tag.fields.slug}
                    onClick={() => {
                      setTags({
                        ...tags,
                        [Tag.fields.slug]: !tags[Tag.fields.slug],
                      })
                    }}
                    color={tags[Tag.fields.slug] ? 'primary' : 'default'}
                    label={Tag.fields.title}
                  />
                )
              })}
            </div>
          </div>
        </Grid>
      </Grid>
    </Page>
  )
}

const filterAndSort = (
  FAQs: IFaq[],
  fuse: Fuse<IFaq>,
  search: string,
  tags: Record<string, boolean>,
): IFaq[] => {
  const searchResults = search.length
    ? fuse.search(search).map((result) => result.item)
    : FAQs

  // match against tags
  const filteredFAQs = searchResults.filter((Faq) => {
    const filterTags = Object.keys(tags).filter((tag) => tags[tag])

    if (filterTags.length > 0 && Faq.fields.tags) {
      for (const tag of Faq.fields.tags) {
        if (filterTags.includes((tag as ITag).fields.slug)) {
          return true
        }
      }
      return false
    }

    return true
  })

  // use fuse sort if we searched
  if (search.length) {
    return filteredFAQs
  }

  // otherwise, stable sort by order, then createdAt
  return filteredFAQs.sort((a, b) => {
    if (a.fields.order && !b.fields.order) {
      return -1
    }
    if (!a.fields.order && b.fields.order) {
      return 1
    }

    if (!a.fields.order || !b.fields.order) {
      if (!a.sys.createdAt || !b.sys.createdAt) {
        return 1
      }
      return a.sys.createdAt > b.sys.createdAt ? 1 : -1
    }
    return a.fields.order > b.fields.order ? 1 : -1
  })
}

export default FAQPage

export const getStaticProps: GetStaticProps<FAQPageProps> = async () => {
  const FAQSeries = await fetchSeries('faq-entries')
  const FAQs = (FAQSeries?.fields.entries || []).filter(isEntry).filter(isFaq)
  const Tags = (await fetchTags()) || []
  return {
    props: {
      FAQs,
      Tags,
    },
    revalidate: 20,
  }
}
